import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { ClipboardList, Search, Handshake, Star, Plus, Minus } from "lucide-react";

const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay }} className={className}>
      {children}
    </motion.div>
  );
};

const STEPS = [
  { icon: ClipboardList, num: "01", title: "Share the details", body: "Tell us about your property in about 60 seconds — condition, timeline, and what you're hoping for." },
  { icon: Search, num: "02", title: "Review your options", body: "We look at your situation and lay out the selling paths available to you, with zero obligation." },
  { icon: Handshake, num: "03", title: "Choose your path", body: "Pick what works best for you, on your timeline. No listing, no pressure, no surprises." },
];

export const HowItWorks = () => (
  <section id="options" className="bg-cream-deep border-y border-ink/10" data-testid="how-it-works">
    <div className="max-w-[1180px] mx-auto px-6 md:px-10 py-24 md:py-32">
      <Reveal className="max-w-2xl mb-16">
        <span className="font-mono text-[11px] tracking-[0.35em] uppercase text-gold">How it works</span>
        <h2 className="font-serif font-light text-5xl md:text-6xl tracking-tight text-ink mt-6 leading-[0.95]">
          Three simple <span className="italic text-gold">steps</span>
        </h2>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-6">
        {STEPS.map((s, i) => (
          <Reveal key={s.num} delay={i * 0.12}>
            <div className="group bg-white border border-ink/10 p-8 h-full hover:border-gold transition-colors duration-300 rounded-lg">
              <div className="flex items-center justify-between mb-8">
                <div className="w-12 h-12 rounded-full bg-evergreen text-cream flex items-center justify-center group-hover:bg-gold transition-colors duration-300">
                  <s.icon size={20} />
                </div>
                <span className="font-serif text-5xl text-ink/10">{s.num}</span>
              </div>
              <h3 className="font-serif text-2xl text-ink">{s.title}</h3>
              <p className="text-ink-soft mt-3 leading-relaxed">{s.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const QUOTES = [
  { q: "They laid out every option without any pressure. I finally understood what I could do with my inherited house.", n: "Marcus D.", loc: "Midtown, Columbus" },
  { q: "The house needed a lot of work and I thought that ruled me out. It didn't. The whole thing took a week.", n: "Linda R.", loc: "Wynnton, Columbus" },
  { q: "Straightforward, local, and genuinely helpful. No agents circling, no listing headaches.", n: "James & Carol P.", loc: "North Columbus" },
];

export const Testimonials = () => (
  <section className="bg-evergreen text-cream" data-testid="testimonials">
    <div className="max-w-[1180px] mx-auto px-6 md:px-10 py-24 md:py-32">
      <Reveal className="max-w-2xl mb-16">
        <span className="font-mono text-[11px] tracking-[0.35em] uppercase text-gold-light">From homeowners</span>
        <h2 className="font-serif font-light text-5xl md:text-6xl tracking-tight text-cream mt-6 leading-[0.95]">
          Neighbors we've <span className="italic text-gold-light">helped</span>
        </h2>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-6">
        {QUOTES.map((t, i) => (
          <Reveal key={t.n} delay={i * 0.12}>
            <div className="border border-cream/15 p-8 h-full flex flex-col rounded-lg bg-evergreen-light/40">
              <div className="flex gap-1 text-gold-light mb-6">
                {[...Array(5)].map((_, k) => <Star key={k} size={15} fill="currentColor" />)}
              </div>
              <p className="font-serif text-xl leading-relaxed text-cream/90 flex-1">“{t.q}”</p>
              <div className="mt-6 pt-6 border-t border-cream/10">
                <p className="text-cream font-medium">{t.n}</p>
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-cream/50 mt-1">{t.loc}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const FAQS = [
  ["Do I have to sell if I fill out the form?", "Not at all. There is zero obligation. The form simply helps us understand your situation so we can walk you through the options available to you."],
  ["Does the condition of my home matter?", "We consider homes in any condition — move-in ready, needs repairs, or a full renovation project. You don't need to fix anything first."],
  ["How is this different from listing with an agent?", "We help you explore alternatives to a traditional listing. That can mean a faster timeline, no repairs, and no showings — you choose what fits."],
  ["What areas do you cover?", "We focus on Columbus, Georgia and the surrounding communities. Local knowledge is a big part of how we help."],
];

export const FAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="max-w-[900px] mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="faq">
      <Reveal className="mb-14">
        <span className="font-mono text-[11px] tracking-[0.35em] uppercase text-gold">Questions</span>
        <h2 className="font-serif font-light text-5xl md:text-6xl tracking-tight text-ink mt-6 leading-[0.95]">
          Good to <span className="italic text-gold">know</span>
        </h2>
      </Reveal>
      <div className="divide-y divide-ink/10 border-t border-b border-ink/10">
        {FAQS.map(([q, a], i) => (
          <div key={i} data-testid={`faq-item-${i}`}>
            <button onClick={() => setOpen(open === i ? -1 : i)}
              className="w-full flex items-center justify-between gap-4 py-6 text-left group">
              <span className="font-serif text-xl md:text-2xl text-ink group-hover:text-gold transition-colors">{q}</span>
              <span className="text-gold shrink-0">{open === i ? <Minus size={20} /> : <Plus size={20} />}</span>
            </button>
            <motion.div initial={false} animate={{ height: open === i ? "auto" : 0, opacity: open === i ? 1 : 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }} className="overflow-hidden">
              <p className="text-ink-soft leading-relaxed pb-6 max-w-2xl">{a}</p>
            </motion.div>
          </div>
        ))}
      </div>
    </section>
  );
};

export const FinalCTA = () => (
  <section className="bg-evergreen text-cream" data-testid="final-cta">
    <div className="max-w-[1180px] mx-auto px-6 md:px-10 py-24 md:py-28 text-center">
      <Reveal>
        <span className="font-mono text-[11px] tracking-[0.35em] uppercase text-gold-light">No obligation</span>
        <h2 className="font-serif font-light text-5xl md:text-7xl tracking-tight text-cream mt-6 leading-[0.95] max-w-3xl mx-auto">
          See what your <span className="italic text-gold-light">options</span> are today
        </h2>
        <p className="text-cream/70 text-lg mt-6 max-w-xl mx-auto">
          It takes about 60 seconds and there's no commitment. Just clarity from a local Columbus team.
        </p>
        <a href="#start" data-testid="final-cta-btn"
          className="inline-flex items-center gap-3 bg-gold text-white font-mono text-[12px] tracking-[0.15em] uppercase px-10 py-5 mt-10 hover:bg-gold-hover transition-colors">
          Explore My Options
        </a>
      </Reveal>
    </div>
  </section>
);
